/* ============================================================
 * AYU /termin – Kennenlern-Gespräch Buchungsseite
 * Design: Sacred Modern (Gold + Olive)
 * Analog zu MENTO /termin
 * ============================================================ */

import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { motion, useInView } from "framer-motion";
import { IMAGES } from "@/lib/constants";
import {
  Heart,
  Clock,
  ShieldCheck,
  MessageCircle,
  Sparkles,
  Headphones,
  Monitor,
  BookOpen,
  ArrowLeft,
  CheckCircle,
  Star,
  CalendarDays,
  Leaf,
} from "lucide-react";

/* ---------- Animated Section Wrapper ---------- */
function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ============================================================
 * MAIN PAGE COMPONENT
 * ============================================================ */
export default function Termin() {
  const [scrolled, setScrolled] = useState(false);
  const calendlyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const initCalendly = () => {
      const w = window as any;
      if (w.Calendly && calendlyRef.current) {
        calendlyRef.current.innerHTML = "";
        w.Calendly.initInlineWidget({
          url: "https://calendly.com/d/cxvv-tks-qfv/rr-team-beratung-ayu?hide_event_type_details=1&hide_gdpr_banner=1&background_color=ffff&text_color=1a1a1a&primary_color=B8762B",
          parentElement: calendlyRef.current,
        });
      }
    };
    if ((window as any).Calendly) {
      initCalendly();
    } else {
      const interval = setInterval(() => {
        if ((window as any).Calendly) {
          clearInterval(interval);
          initCalendly();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  return (
    <div className="min-h-screen bg-[oklch(0.98_0.005_85)]">
      {/* ========== MINIMAL HEADER ========== */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[oklch(0.14_0.02_65/0.95)] backdrop-blur-xl shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="container flex items-center justify-between py-4">
          <Link href="/">
            <img
              src={IMAGES.logo}
              alt="AYU"
              className="h-10 w-auto"
            />
          </Link>
          <Link
            href="/"
            className="flex items-center gap-2 text-sm font-medium text-gold hover:text-gold-light transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>
        </div>
      </header>

      {/* ========== HERO SECTION ========== */}
      <section className="relative pt-32 pb-20 bg-[oklch(0.14_0.02_65)] overflow-hidden">
        {/* Mandala Background */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url(${IMAGES.mandala})`,
            backgroundSize: "800px",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[oklch(0.14_0.02_65)]" />

        <div className="container relative z-10 text-center max-w-3xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-olive/10 border border-olive/20 mb-8">
              <Heart className="w-4 h-4 text-olive-light" />
              <span className="text-sm font-medium text-olive-light">
                Du bist fast da
              </span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-[oklch(0.95_0.02_85)] leading-tight mb-6">
              Dein persönliches{" "}
              <span className="italic text-gold">Kennenlerngespräch</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="text-lg md:text-xl text-[oklch(0.75_0.02_85)] leading-relaxed mb-8 max-w-2xl mx-auto">
              15 Minuten, die alles verändern können. Kein Verkaufsdruck.
              Kein Skript. Einfach ein ehrliches Gespräch über deine
              Gesundheit und dein Wohlbefinden.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="flex flex-wrap items-center justify-center gap-6 mb-10 text-sm text-[oklch(0.65_0.02_85)]">
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gold" />
                15 Minuten
              </span>
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-gold" />
                100% vertraulich
              </span>
              <span className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-gold" />
                Kein Verkaufsdruck
              </span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <a
              href="#calendly"
              className="inline-flex items-center gap-2 px-8 py-4 bg-olive text-white font-semibold rounded-lg hover:bg-olive-light transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              <CalendarDays className="w-5 h-5" />
              Jetzt Termin wählen
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== WAS DICH ERWARTET ========== */}
      <section className="py-20 bg-[oklch(0.98_0.005_85)]">
        <div className="container max-w-5xl mx-auto">
          <AnimatedSection>
            <p className="text-center text-sm font-semibold tracking-[0.2em] uppercase text-olive mb-4">
              Was dich erwartet
            </p>
            <h2 className="text-center font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)] mb-4">
              Kein Arzttermin. Ein{" "}
              <span className="italic text-olive">
                Gespräch auf Augenhöhe.
              </span>
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {[
              {
                icon: MessageCircle,
                title: "Wir hören dir zu",
                text: "Die ersten 15 Minuten gehören dir. Erzähl uns, was dich beschäftigt — deine Gesundheit, deine Fragen, deine Ziele. Ohne Zeitdruck, ohne Bewertung. Wir sind da.",
              },
              {
                icon: Sparkles,
                title: "Wir zeigen dir den Weg",
                text: "Basierend auf deiner Situation erklären wir dir, wie AYU konkret helfen kann — und ob es überhaupt das Richtige für dich ist. Ehrlich und transparent.",
              },
              {
                icon: Heart,
                title: "Du entscheidest",
                text: "Am Ende des Gesprächs entscheidest du — ohne Druck, ohne Eile. Und selbst wenn AYU nicht das Richtige ist, gehst du mit mehr Klarheit über deine Gesundheit.",
              },
            ].map((card, i) => (
              <AnimatedSection key={card.title} delay={i * 0.15}>
                <div className="bg-white rounded-xl p-8 shadow-sm border border-[oklch(0.9_0.01_85)] hover:shadow-md transition-shadow h-full">
                  <div className="w-12 h-12 rounded-lg bg-olive/10 flex items-center justify-center mb-5">
                    <card.icon className="w-6 h-6 text-olive" />
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[oklch(0.2_0.02_65)] mb-3">
                    {card.title}
                  </h3>
                  <p className="text-[oklch(0.45_0.02_65)] leading-relaxed">
                    {card.text}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16 bg-[oklch(0.96_0.008_85)]">
        <div className="container max-w-3xl mx-auto">
          <AnimatedSection>
            <div className="bg-white rounded-2xl p-10 shadow-sm border border-[oklch(0.9_0.01_85)]">
              <div className="flex gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 text-gold fill-gold"
                  />
                ))}
              </div>
              <blockquote className="font-playfair text-xl md:text-2xl italic text-[oklch(0.25_0.02_65)] leading-relaxed mb-8">
                „Ich war so nervös vor dem Gespräch. Aber nach 2 Minuten
                fühlte es sich an wie ein Gespräch mit einer guten
                Freundin. Keine Fachbegriffe, kein Druck — einfach
                jemand, der versteht und zuhört."
              </blockquote>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                  <span className="text-sm font-bold text-olive">
                    S.M.
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-[oklch(0.2_0.02_65)]">
                    Sandra M.
                  </p>
                  <p className="text-sm text-[oklch(0.55_0.02_65)]">
                    AYU-Nutzerin seit 2025
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== CALENDLY EMBED ========== */}
      <section id="calendly" className="py-20 bg-[oklch(0.98_0.005_85)]">
        <div className="container max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-olive/10 border border-olive/20 mb-6">
              <CalendarDays className="w-4 h-4 text-olive-light" />
              <span className="text-sm font-medium text-olive">
                Termin wählen
              </span>
            </div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)] mb-4">
              Wähle deinen{" "}
              <span className="italic text-olive">Wunschtermin</span>
            </h2>
            <p className="text-[oklch(0.45_0.02_65)] mb-10 max-w-xl mx-auto">
              Such dir einen Zeitpunkt aus, der für dich passt. Wir melden
              uns per Zoom bei dir.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            {/* Calendly Inline Widget */}
            <div className="bg-white rounded-2xl shadow-sm border border-[oklch(0.9_0.01_85)] overflow-hidden">
              <div
                ref={calendlyRef}
                style={{ minWidth: "320px", height: "700px" }}
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== VOR DEINEM TERMIN ========== */}
      <section className="py-20 bg-[oklch(0.14_0.02_65)] relative overflow-hidden">
        {/* Mandala Background */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url(${IMAGES.mandala})`,
            backgroundSize: "600px",
            backgroundPosition: "center",
          }}
        />

        <div className="container relative z-10 max-w-5xl mx-auto">
          <AnimatedSection>
            <p className="text-center text-sm font-semibold tracking-[0.2em] uppercase text-gold mb-4">
              Vor deinem Termin
            </p>
            <h2 className="text-center font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.95_0.02_85)] mb-14">
              So holst du das{" "}
              <span className="italic text-gold">Beste</span> heraus
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {[
              {
                icon: Monitor,
                title: "Nimm dir Ruhe",
                text: "Such dir einen ruhigen Ort, wo du ungestört sprechen kannst. Mach dir vielleicht einen Tee — das hier soll sich gut anfühlen.",
              },
              {
                icon: Headphones,
                title: "Kopfhörer bereit",
                text: "Das Gespräch findet per Zoom statt. Kopfhörer helfen, damit du dich voll auf das Gespräch konzentrieren kannst.",
              },
              {
                icon: BookOpen,
                title: "Sei offen",
                text: "Du musst nichts vorbereiten. Komm einfach so, wie du bist. Je ehrlicher du bist, desto besser können wir dir helfen.",
              },
            ].map((card, i) => (
              <AnimatedSection key={card.title} delay={i * 0.15}>
                <div className="bg-[oklch(0.18_0.02_65)] rounded-xl p-8 border border-[oklch(0.25_0.02_65)] hover:border-gold/30 transition-colors h-full">
                  <div className="w-12 h-12 rounded-lg bg-olive/15 flex items-center justify-center mb-5">
                    <card.icon className="w-6 h-6 text-olive-light" />
                  </div>
                  <h3 className="font-playfair text-xl font-bold text-[oklch(0.92_0.02_85)] mb-3">
                    {card.title}
                  </h3>
                  <p className="text-[oklch(0.65_0.02_85)] leading-relaxed">
                    {card.text}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Wichtig zu wissen */}
          <AnimatedSection delay={0.3}>
            <div className="bg-[oklch(0.18_0.02_65)] rounded-xl p-8 border border-gold/20 max-w-3xl mx-auto">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-olive/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-5 h-5 text-olive-light" />
                </div>
                <div>
                  <h4 className="font-semibold text-[oklch(0.92_0.02_85)] mb-2">
                    Wichtig zu wissen
                  </h4>
                  <p className="text-[oklch(0.65_0.02_85)] leading-relaxed">
                    Dieses Gespräch ist für dich — nicht für uns. Wenn AYU
                    nicht das Richtige für dich ist, sagen wir dir das
                    ehrlich. Unser Ziel ist es, dass du mit Klarheit aus
                    dem Gespräch gehst, egal wie du dich entscheidest.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== BETREUER TEAM ========== */}
      <section className="py-20 bg-[oklch(0.98_0.005_85)]">
        <div className="container max-w-5xl mx-auto">
          <AnimatedSection>
            <p className="text-center text-sm font-semibold tracking-[0.2em] uppercase text-olive mb-4">
              Echte Menschen. Echtes Verständnis.
            </p>
            <h2 className="text-center font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)] mb-4">
              Dein{" "}
              <span className="italic text-olive">Betreuer-Team</span>
            </h2>
            <p className="text-center text-[oklch(0.45_0.02_65)] mb-14 max-w-2xl mx-auto">
              Du buchst keinen anonymen Termin. Du triffst Menschen, die zuhören, verstehen und dich ehrlich beraten.
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Lilli",
                role: "Ernährungsberaterin & Mentalcoach",
                image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/lilli-real_3821713a.png",
                description: "Zweifache Mama und leidenschaftlich sportbegeistert. Seit 2015 begleitet sie Menschen auf ihrem Weg zu mehr Gesundheit, Energie und nachhaltiger Balance.",
                tags: ["Ernährung", "Mentalcoaching", "Ganzheitlich"],
              },
              {
                name: "Silke",
                role: "Ganzheitliche Ernährungsberaterin & Coach",
                image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/silke-cropped_1f8d8adc.png",
                description: "Meine Vision ist es, Menschen auf ganzheitlicher Ebene zu unterstützen, damit sie ihr eigenes Körpergefühl zurückerlangen.",
                tags: ["Ernährung", "Coaching", "Körperbewusstsein"],
              },
              {
                name: "Susan",
                role: "Ernährungsberaterin & Gesundheitscoach",
                image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/susan-betreuerin_f35687b6.png",
                description: "Durch ihre eigene Krebserkrankung und Schilddrüsenprobleme hat Susan erfahren, wie wichtig bewusste Ernährung für unser Leben ist.",
                tags: ["Ernährung", "Gesundheitscoaching", "Prävention"],
              },
            ].map((person, i) => (
              <AnimatedSection key={person.name} delay={i * 0.15}>
                <div className="group rounded-2xl overflow-hidden bg-white border border-[oklch(0.9_0.01_85)] hover:shadow-lg transition-all h-full">
                  <div className="relative overflow-hidden aspect-[4/5]">
                    <img
                      src={person.image}
                      alt={person.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[oklch(0.2_0.02_65/0.3)]" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-playfair text-xl font-bold text-[oklch(0.2_0.02_65)]">
                      {person.name}
                    </h3>
                    <p className="text-sm font-semibold text-olive mt-1 mb-3">
                      {person.role}
                    </p>
                    <p className="text-sm text-[oklch(0.45_0.02_65)] leading-relaxed mb-4">
                      {person.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {person.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-olive/8 text-olive border border-olive/15"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="py-20 bg-[oklch(0.98_0.005_85)] text-center">
        <div className="container max-w-2xl mx-auto">
          <AnimatedSection>
            <div className="w-14 h-14 rounded-full bg-olive/10 flex items-center justify-center mx-auto mb-6">
              <Sparkles className="w-7 h-7 text-olive" />
            </div>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[oklch(0.2_0.02_65)] mb-4">
              Der schwierigste Schritt ist der erste.
            </h2>
            <p className="text-[oklch(0.45_0.02_65)] mb-8 max-w-lg mx-auto leading-relaxed">
              Und den hast du schon fast gemacht. Scroll nach oben und
              wähle deinen Termin — wir freuen uns auf dich.
            </p>
            <a
              href="#calendly"
              className="inline-flex items-center gap-2 px-8 py-4 bg-olive text-white font-semibold rounded-lg hover:bg-olive-light transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              <CalendarDays className="w-5 h-5" />
              Jetzt Termin wählen
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* ========== MINIMAL FOOTER ========== */}
      <footer className="py-8 bg-[oklch(0.14_0.02_65)] border-t border-[oklch(0.22_0.02_65)]">
        <div className="container flex flex-col md:flex-row items-center justify-between gap-4">
          <Link href="/">
            <img
              src={IMAGES.logo}
              alt="AYU"
              className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity"
            />
          </Link>
          <p className="text-sm text-[oklch(0.5_0.02_85)]">
            &copy; {new Date().getFullYear()} AYU Healthtech LIMITED
          </p>
          <div className="flex items-center gap-6">
            <Link
              href="/impressum"
              className="text-sm text-[oklch(0.5_0.02_85)] hover:text-gold transition-colors"
            >
              Impressum
            </Link>
            <Link
              href="/datenschutz"
              className="text-sm text-[oklch(0.5_0.02_85)] hover:text-gold transition-colors"
            >
              Datenschutz
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
